import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { ClioService, FilingReportItem } from "../../../api";
import { FILING_REPORT_STATUS_OPTIONS } from "./filingReportStatusOptions";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const STATUS_FILTER_ALL = "";
const STATUS_FILTER_NONE = "__none__";

export default function FilingReport() {
  const [reportData, setReportData] = useState<FilingReportItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [loading, setLoading] = useState(false);
  const [savingTaskId, setSavingTaskId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.getApiClioFilingReport()
      .then(setReportData)
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading filing report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filteredData = useMemo(() => {
    let data = reportData;

    if (statusFilter === STATUS_FILTER_NONE) {
      data = data.filter((row) => !row.status);
    } else if (statusFilter) {
      data = data.filter((row) => row.status === statusFilter);
    }

    if (!searchTerm.trim()) {
      return data;
    }

    const lower = searchTerm.toLowerCase();
    return data.filter(
      (row) =>
        row.caseName?.toLowerCase().includes(lower) ||
        row.caseTeam?.toLowerCase().includes(lower) ||
        row.taskName?.toLowerCase().includes(lower) ||
        row.status?.toLowerCase().includes(lower)
    );
  }, [reportData, searchTerm, statusFilter]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleStatusChange = async (row: FilingReportItem, status: string) => {
    const nextStatus = status || null;
    const previousStatus = row.status ?? null;

    setReportData((prev) =>
      prev.map((item) =>
        item.taskId === row.taskId ? { ...item, status: nextStatus } : item
      )
    );

    setSavingTaskId(row.taskId);
    try {
      await ClioService.postApiClioSaveFilingReportStatuses([
        {
          taskId: row.taskId,
          matterId: row.matterId,
          status: nextStatus,
        },
      ]);
    } catch {
      setReportData((prev) =>
        prev.map((item) =>
          item.taskId === row.taskId ? { ...item, status: previousStatus } : item
        )
      );
      MySwal.fire({
        icon: "error",
        title: <p>Error saving status.</p>,
      });
    } finally {
      setSavingTaskId(null);
    }
  };

  const isOverdue = (dueDate?: string | null) => {
    if (!dueDate) return false;
    return dayjs(dueDate).isBefore(dayjs(), "day");
  };

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Filing Report
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Pending tasks assigned to Narissa Estrada with filing status tracking.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControl size="small" sx={{ width: 280 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value={STATUS_FILTER_ALL}>All</MenuItem>
              <MenuItem value={STATUS_FILTER_NONE}>No status</MenuItem>
              {FILING_REPORT_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            placeholder="Search cases, team, or tasks..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 320 }, flexGrow: { sm: 1 }, minWidth: 220 }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 600, width: 56 }}>No.</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Case Name</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Case Team</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 240 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No tasks match the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <TableRow key={row.taskId} hover>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>{row.caseName}</TableCell>
                  <TableCell sx={{ whiteSpace: "pre-line", fontSize: "0.8125rem" }}>
                    {row.caseTeam}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{row.taskName}</TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      color: isOverdue(row.dueDate) ? "error.main" : "inherit",
                      fontWeight: isOverdue(row.dueDate) ? 600 : 400,
                    }}
                  >
                    {row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "—"}
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth disabled={savingTaskId === row.taskId}>
                      <Select
                        displayEmpty
                        value={row.status ?? ""}
                        onChange={(e) => handleStatusChange(row, e.target.value)}
                        renderValue={(selected) =>
                          selected ? (
                            selected
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              Select status
                            </Typography>
                          )
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {FILING_REPORT_STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
}
